"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function OurStoryHero() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="our-story-hero" className="relative w-full bg-background overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-[70%] bg-secondary/40 rounded-b-[100px] md:rounded-b-[200px] -z-10" />

      <div className="container mx-auto px-4 md:px-6 pt-24 pb-12 lg:pt-32 lg:pb-16">
        <div ref={textRef} className={`max-w-4xl mx-auto text-center flex flex-col items-center transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-sm font-default font-medium tracking-brand-widest uppercase text-green-accent mb-6 block">
            Our Legacy
          </span>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-default font-light text-foreground tracking-tight mb-8 leading-tight">
            Our Story
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground font-default font-normal leading-relaxed max-w-2xl mb-10">
            Built from purpose. Rooted in something personal.
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-4">
            <Button asChild size="lg" className="rounded-full px-8 bg-green-accent text-primary-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-md">
              <Link to="#heritage" className="font-default tracking-wide">A Big Sister's Love</Link>
            </Button>
            <Button asChild variant="ghost" size="icon" className="rounded-full size-12 text-muted-foreground hover:text-foreground">
              <Link to="#heritage" aria-label="Scroll down">
                <ArrowDown className="size-5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 pb-24 lg:pb-32">
        <div ref={imageRef} className={`relative w-full aspect-[4/3] md:aspect-[21/9] rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl transition-all duration-1000 delay-300 ease-out ${isImageVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-12 scale-95"}`}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/mo hero collage.png" alt="MO Protocol Wellness Story" className="w-full h-full object-cover object-center" loading="lazy" decoding="async" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/mo hero collage.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/mo hero collage.png-wvc-sizes" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
        </div>
      </div>
    </section>;
}